import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
    UserPlus,
    Mail,
    Trash2,
    Clock,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Edit3,
    Users,
    Shield,
    User,
    Key
} from 'lucide-react';
import toast from 'react-hot-toast';

import { apiService } from '../services/api';

function UserManagement() {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [showInviteForm, setShowInviteForm] = useState(false);
    const [showCreateUserForm, setShowCreateUserForm] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    // Get current user to check role
    const { data: currentUserData } = useQuery('current-user', () => apiService.getCurrentUser(), {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false
    });

    // Extract user from nested structure if needed
    const currentUser = currentUserData?.user || currentUserData;

    const isAdmin = currentUser?.role === 'admin';
    const isViewer = currentUser?.role === 'viewer';

    // Debug logging
    useEffect(() => {
        console.log('UserManagement - Current User:', currentUser);
        console.log('UserManagement - Current User Role:', currentUser?.role);
        console.log('UserManagement - Current User Keys:', currentUser ? Object.keys(currentUser) : 'none');
        console.log('UserManagement - isAdmin:', isAdmin);
        console.log('UserManagement - isAdmin calculation:', currentUser?.role === 'admin');
    }, [currentUser, isAdmin]);

    // Query users - NO auto-refresh
    // For non-admins, only show their own profile
    const { data: usersData, isLoading: usersLoading, error: usersError } = useQuery(
        'users',
        () => apiService.getUsers(),
        {
            refetchOnWindowFocus: false,
            enabled: isAdmin // Only fetch all users if admin
        }
    );

    // Debug logging
    useEffect(() => {
        console.log('UserManagement - Users Data:', usersData);
        console.log('UserManagement - Users Error:', usersError);
    }, [usersData, usersError]);

    // Safely handle API response
    let users = Array.isArray(usersData) ? usersData : (Array.isArray(usersData?.users) ? usersData.users : []);

    // For non-admins, filter to show only themselves
    if (!isAdmin && currentUser) {
        users = users.filter(user => user.id === currentUser.id);
        // If no users data yet, show current user
        if (users.length === 0) {
            users = [currentUser];
        }
    }

    // Delete user mutation
    const deleteUserMutation = useMutation(
        apiService.deleteUser,
        {
            onSuccess: () => {
                queryClient.invalidateQueries('users');
                toast.success(t('users.deleteSuccess', 'User deleted successfully'));
            },
            onError: (error) => {
                console.error('Delete user error:', error);
                const message = error.response?.data?.error || t('users.deleteError', 'Failed to delete user');
                toast.error(message);
            }
        }
    );

    const handleDeleteUser = (user) => {
        if (window.confirm(t('users.deleteConfirm', 'Are you sure you want to delete this user? This action cannot be undone.'))) {
            deleteUserMutation.mutate(user.id);
        }
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setShowInviteForm(true);
    };

    const handleChangePassword = (user) => {
        setEditingUser(user);
        setShowChangePasswordModal(true);
    };

    // Query invitations - NO auto-refresh
    const { data: invitationsData, isLoading: invitationsLoading } = useQuery(
        'invitations',
        () => apiService.getInvitations(),
        { refetchOnWindowFocus: false } // Disable auto-refresh
    );

    // Safely handle invitations API response
    const invitations = Array.isArray(invitationsData?.invitations) ? invitationsData.invitations : [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-3 sm:p-6 space-y-4 sm:space-y-6 max-w-full overflow-x-hidden">
            {/* Modern Header */}
            <div className="card">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                    <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-200 via-purple-300 to-indigo-300 flex items-center justify-center shadow-lg">
                            <Users className="w-6 h-6 text-purple-900" />
                        </div>
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                                {isAdmin ? t('users.title', 'User Management') : t('users.myProfile', 'My Profile')}
                            </h1>
                            <p className="text-sm sm:text-base text-gray-600 mt-1">
                                {isAdmin ? t('users.subtitle', 'Manage system users and permissions') : t('users.profileSubtitle', 'Manage your account settings')}
                            </p>
                        </div>
                    </div>
                    {isAdmin && (
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => {
                                    setEditingUser(null);
                                    setShowCreateUserForm(true);
                                }}
                                className="btn-secondary flex items-center space-x-2 whitespace-nowrap"
                            >
                                <UserPlus className="h-4 w-4" />
                                <span>{t('users.createUser', 'Create User')}</span>
                            </button>
                            <button
                                onClick={() => {
                                    setEditingUser(null);
                                    setShowInviteForm(true);
                                }}
                                className="btn-primary flex items-center space-x-2 whitespace-nowrap"
                            >
                                <Mail className="h-4 w-4" />
                                <span>{t('users.inviteUser', 'Invite User')}</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Existing Users */}
            <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900">
                        {t('users.existingUsers', 'Existing Users')}
                    </h2>
                </div>

                {usersLoading ? (
                    <div className="p-6">
                        <div className="animate-pulse space-y-4">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="flex items-center space-x-4">
                                    <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                                    <div className="flex-1">
                                        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-200">
                        {(users || []).map((user) => (
                            <div key={user.id} className="px-6 py-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <div
                                            className={`w-12 h-12 rounded-2xl flex items-center justify-center ${user.role === 'admin'
                                                ? 'bg-gradient-to-br from-red-100 to-red-200'
                                                : user.role === 'operator'
                                                    ? 'bg-gradient-to-br from-yellow-100 to-yellow-200'
                                                    : 'bg-gradient-to-br from-gray-100 to-gray-200'
                                                }`}
                                        >
                                            <span
                                                className={`text-lg font-semibold ${user.role === 'admin'
                                                    ? 'text-red-700'
                                                    : user.role === 'operator'
                                                        ? 'text-yellow-700'
                                                        : 'text-gray-700'
                                                    }`}
                                            >
                                                {((user.full_name || user.email || 'U').charAt(0).toUpperCase())}
                                            </span>
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-2 mb-1">
                                                <div className="font-semibold text-gray-900">
                                                    {user.full_name || user.email || 'Unknown User'}
                                                </div>
                                                <span
                                                    className={`badge ${user.role === 'admin'
                                                        ? 'badge-error'
                                                        : user.role === 'operator'
                                                            ? 'badge-warning'
                                                            : 'badge-primary'
                                                        }`}
                                                >
                                                    <Shield className="w-3 h-3 mr-1" />
                                                    {t(`roles.${user.role}`, user.role)}
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                                                <div className="flex items-center space-x-1">
                                                    <Mail className="w-3 h-3" />
                                                    <span>{user.email}</span>
                                                </div>
                                                <div className="flex items-center space-x-1">
                                                    <Clock className="w-3 h-3" />
                                                    <span>{new Date(user.created_at).toLocaleDateString()}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                        <button
                                            onClick={() => handleChangePassword(user)}
                                            className="btn-ghost p-2 text-blue-600"
                                            title={t('users.changePassword', 'Change Password')}
                                        >
                                            <Key className="h-4 w-4" />
                                        </button>
                                        {isAdmin && (
                                            <>
                                                <button
                                                    onClick={() => handleEditUser(user)}
                                                    className="btn-ghost p-2 text-primary"
                                                    title={t('common.edit')}
                                                >
                                                    <Edit3 className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteUser(user)}
                                                    className="btn-ghost p-2 text-red-600 hover:text-red-700"
                                                    title={t('common.delete')}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Pending Invitations - Only for admins */}
            {isAdmin && (
                <div className="bg-white shadow rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-medium text-gray-900">
                            {t('users.pendingInvitations', 'Pending Invitations')}
                        </h2>
                    </div>

                    {invitationsLoading ? (
                        <div className="p-6">
                            <div className="animate-pulse space-y-4">
                                {[...Array(2)].map((_, i) => (
                                    <div key={i} className="flex items-center space-x-4">
                                        <div className="h-8 w-8 bg-gray-200 rounded"></div>
                                        <div className="flex-1">
                                            <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                                            <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : invitations.length === 0 ? (
                        <div className="p-6 text-center">
                            <Mail className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">
                                {t('users.noPendingInvitations', 'No pending invitations')}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {t('users.inviteUsersToGetStarted', 'Invite users to get them started.')}
                            </p>
                        </div>
                    ) : (
                        <InvitationsList invitations={invitations} />
                    )}
                </div>
            )}

            {/* Invite/Edit Form Modal */}
            {showInviteForm && (
                <InviteUserModal
                    user={editingUser}
                    onClose={() => {
                        setShowInviteForm(false);
                        setEditingUser(null);
                    }}
                />
            )}

            {/* Create User Modal */}
            {showCreateUserForm && (
                <CreateUserModal
                    onClose={() => {
                        setShowCreateUserForm(false);
                        setEditingUser(null);
                    }}
                />
            )}

            {/* Change Password Modal */}
            {showChangePasswordModal && (
                <ChangePasswordModal
                    user={editingUser}
                    onClose={() => {
                        setShowChangePasswordModal(false);
                        setEditingUser(null);
                    }}
                />
            )}
        </div>
    );
}

function InvitationsList({ invitations }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const cancelInviteMutation = useMutation(
        (invitationId) => apiService.cancelInvitation(invitationId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('invitations');
                toast.success(t('users.invitationCanceled', 'Invitation canceled'));
            },
            onError: () => {
                toast.error(t('users.failedToCancelInvitation', 'Failed to cancel invitation'));
            }
        }
    );

    const handleCancelInvitation = (invitation) => {
        if (window.confirm(t('users.confirmCancelInvitation', 'Are you sure you want to cancel this invitation?'))) {
            cancelInviteMutation.mutate(invitation.id);
        }
    };

    return (
        <div className="divide-y divide-gray-200">
            {Array.isArray(invitations) && invitations.map((invitation) => (
                <div key={invitation.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <div className="h-8 w-8 rounded bg-blue-100 flex items-center justify-center">
                                {invitation.isExpired ? (
                                    <XCircle className="h-4 w-4 text-red-500" />
                                ) : (
                                    <Clock className="h-4 w-4 text-blue-500" />
                                )}
                            </div>
                            <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                    {invitation.fullName}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {invitation.email}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            <div className="text-right">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${invitation.role === 'admin' ? 'bg-red-100 text-red-800' :
                                    invitation.role === 'operator' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-gray-100 text-gray-800'
                                    }`}>
                                    {t(`roles.${invitation.role}`, invitation.role)}
                                </span>
                                <div className="text-xs text-gray-500 mt-1">
                                    {invitation.isExpired ? (
                                        <span className="text-red-500">
                                            {t('users.expired', 'Expired')}
                                        </span>
                                    ) : (
                                        <span>
                                            {t('users.expires', 'Expires')}: {new Date(invitation.expiresAt).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-gray-400 mt-1">
                                    {t('users.invitedBy', 'By')}: {invitation.invitedBy}
                                </div>
                            </div>
                            <button
                                onClick={() => handleCancelInvitation(invitation)}
                                disabled={cancelInviteMutation.isLoading}
                                className="text-red-600 hover:text-red-800 disabled:opacity-50"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function InviteUserModal({ user, onClose }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const isEditing = !!user;
    const [formData, setFormData] = useState({
        email: user?.email || '',
        fullName: user?.full_name || '',
        role: user?.role || 'viewer'
    });
    const [selectedLocations, setSelectedLocations] = useState([]);

    // Query locations for access management
    const { data: locationsData } = useQuery('locations', () => apiService.getLocations(), {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false
    });
    const locations = Array.isArray(locationsData) ? locationsData : (Array.isArray(locationsData?.locations) ? locationsData.locations : []);

    // Query user's current locations if editing
    const { data: userLocationsData } = useQuery(
        ['user-locations', user?.id],
        () => apiService.getUserLocations(user.id),
        {
            enabled: isEditing && !!user?.id,
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false
        }
    );

    // Set initial selected locations when editing
    useEffect(() => {
        if (isEditing && userLocationsData?.locations) {
            setSelectedLocations(userLocationsData.locations.map(loc => loc.id));
        }
    }, [isEditing, userLocationsData]);

    // Update form data when user prop changes
    useEffect(() => {
        if (user) {
            setFormData({
                email: user.email || '',
                fullName: user.full_name || '',
                role: user.role || 'viewer'
            });
        } else {
            setFormData({
                email: '',
                fullName: '',
                role: 'viewer'
            });
            setSelectedLocations([]);
        }
    }, [user]);

    const inviteMutation = useMutation(
        (data) => apiService.inviteUser(data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('invitations');
                toast.success(t('users.invitationSent', 'Invitation sent successfully'));
                onClose();
            },
            onError: (error) => {
                const message = error.response?.data?.error || t('users.failedToSendInvitation', 'Failed to send invitation');
                toast.error(message);
            }
        }
    );

    const updateUserMutation = useMutation(
        async ({ userId, userData, locationIds }) => {
            await apiService.updateUser(userId, userData);
            if (Array.isArray(locationIds)) {
                await apiService.updateUserLocations(userId, locationIds);
            }
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('users');
                queryClient.invalidateQueries(['user-locations', user?.id]);
                toast.success(t('users.updateSuccess', 'User updated successfully'));
                onClose();
            },
            onError: (error) => {
                const message = error.response?.data?.error || t('users.updateError', 'Failed to update user');
                toast.error(message);
            }
        }
    );

    const handleSubmit = (e) => {
        e.preventDefault();

        if (isEditing) {
            updateUserMutation.mutate({
                userId: user.id,
                userData: {
                    full_name: formData.fullName,
                    role: formData.role
                },
                locationIds: selectedLocations
            });
        } else {
            inviteMutation.mutate(formData);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                    <div>
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                            <UserPlus className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="mt-3 text-center sm:mt-5">
                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                {isEditing ? t('users.editUser', 'Edit User') : t('users.inviteNewUser', 'Invite New User')}
                            </h3>
                        </div>
                    </div>

                    <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t('auth.fullName')}
                            </label>
                            <input
                                type="text"
                                name="fullName"
                                required
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                value={formData.fullName}
                                onChange={handleChange}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t('auth.emailAddress')}
                            </label>
                            <input
                                type="email"
                                name="email"
                                required={!isEditing}
                                disabled={isEditing}
                                className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${isEditing ? 'bg-gray-100' : ''}`}
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t('users.role', 'Role')}
                            </label>
                            <select
                                name="role"
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                value={formData.role}
                                onChange={handleChange}
                            >
                                <option value="viewer">{t('roles.viewer', 'Viewer')}</option>
                                <option value="operator">{t('roles.operator', 'Operator')}</option>
                                <option value="admin">{t('roles.admin', 'Administrator')}</option>
                            </select>
                        </div>

                        {/* Location Access Management */}
                        {isEditing && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('users.locationAccess', 'Location Access')}
                                </label>
                                <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                                    {locations.map(location => (
                                        <label key={location.id} className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                checked={selectedLocations.includes(location.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedLocations([...selectedLocations, location.id]);
                                                    } else {
                                                        setSelectedLocations(selectedLocations.filter(id => id !== location.id));
                                                    }
                                                }}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-700">{location.name}</span>
                                        </label>
                                    ))}
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    {t('users.locationAccessHelp', 'Select which locations this user can access')}
                                </p>
                            </div>
                        )}

                        <div className="mt-6 flex space-x-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                {t('common.cancel', 'Cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={inviteMutation.isLoading || updateUserMutation.isLoading}
                                className="flex-1 bg-blue-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                                {(inviteMutation.isLoading || updateUserMutation.isLoading) ? (
                                    t('common.sending', 'Sending...')
                                ) : isEditing ? (
                                    t('common.update', 'Update')
                                ) : (
                                    t('users.sendInvitation', 'Send Invitation')
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

function ChangePasswordModal({ user, onClose }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });

    const changePasswordMutation = useMutation(
        ({ userId, password }) => apiService.changeUserPassword(userId, password),
        {
            onSuccess: () => {
                toast.success(t('users.passwordChangedSuccess', 'Password changed successfully'));
                onClose();
            },
            onError: (error) => {
                const message = error.response?.data?.error || t('users.passwordChangeError', 'Failed to change password');
                toast.error(message);
            }
        }
    );

    const handleSubmit = (e) => {
        e.preventDefault();

        if (formData.newPassword !== formData.confirmPassword) {
            toast.error(t('auth.passwordsDoNotMatch', 'Passwords do not match'));
            return;
        }

        if (formData.newPassword.length < 6) {
            toast.error(t('auth.passwordTooShort', 'Password must be at least 6 characters'));
            return;
        }

        changePasswordMutation.mutate({
            userId: user.id,
            password: formData.newPassword
        });
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6">
                    <div>
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                            <Key className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="mt-3 text-center sm:mt-5">
                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                {t('users.changePasswordFor', 'Change Password for')} {user.full_name || user.email}
                            </h3>
                        </div>
                    </div>

                    <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t('auth.newPassword', 'New Password')}
                            </label>
                            <input
                                type="password"
                                required
                                minLength={6}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                value={formData.newPassword}
                                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t('auth.confirmPassword', 'Confirm Password')}
                            </label>
                            <input
                                type="password"
                                required
                                minLength={6}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                value={formData.confirmPassword}
                                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                            />
                        </div>

                        <div className="mt-6 flex space-x-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                {t('common.cancel', 'Cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={changePasswordMutation.isLoading}
                                className="flex-1 bg-blue-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                                {changePasswordMutation.isLoading ? t('common.changing', 'Changing...') : t('users.changePassword', 'Change Password')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

function CreateUserModal({ onClose }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState({
        email: '',
        fullName: '',
        password: '',
        confirmPassword: '',
        role: 'viewer'
    });

    const createUserMutation = useMutation(
        (data) => apiService.createUser(data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('users');
                toast.success(t('users.userCreatedSuccess', 'User created successfully'));
                onClose();
            },
            onError: (error) => {
                const message = error.response?.data?.error || t('users.userCreateError', 'Failed to create user');
                toast.error(message);
            }
        }
    );

    const handleSubmit = (e) => {
        e.preventDefault();

        if (formData.password !== formData.confirmPassword) {
            toast.error(t('auth.passwordsDoNotMatch', 'Passwords do not match'));
            return;
        }

        if (formData.password.length < 6) {
            toast.error(t('auth.passwordTooShort', 'Password must be at least 6 characters'));
            return;
        }

        createUserMutation.mutate({
            email: formData.email,
            full_name: formData.fullName,
            password: formData.password,
            role: formData.role
        });
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                    <div>
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                            <UserPlus className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="mt-3 text-center sm:mt-5">
                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                {t('users.createNewUser', 'Create New User')}
                            </h3>
                            <p className="mt-2 text-sm text-gray-500">
                                {t('users.createUserHelp', 'Create a user account without sending an invitation email')}
                            </p>
                        </div>
                    </div>

                    <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t('auth.fullName')}
                            </label>
                            <input
                                type="text"
                                required
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                value={formData.fullName}
                                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t('auth.emailAddress')}
                            </label>
                            <input
                                type="email"
                                required
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t('auth.password', 'Password')}
                            </label>
                            <input
                                type="password"
                                required
                                minLength={6}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t('auth.confirmPassword', 'Confirm Password')}
                            </label>
                            <input
                                type="password"
                                required
                                minLength={6}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                value={formData.confirmPassword}
                                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t('users.role', 'Role')}
                            </label>
                            <select
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                value={formData.role}
                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            >
                                <option value="viewer">{t('roles.viewer', 'Viewer')}</option>
                                <option value="operator">{t('roles.operator', 'Operator')}</option>
                                <option value="admin">{t('roles.admin', 'Administrator')}</option>
                            </select>
                        </div>

                        <div className="mt-6 flex space-x-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                {t('common.cancel', 'Cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={createUserMutation.isLoading}
                                className="flex-1 bg-green-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                            >
                                {createUserMutation.isLoading ? t('common.creating', 'Creating...') : t('users.createUser', 'Create User')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default UserManagement;
